import { type ReactNode } from 'react'
import cx from 'classnames'
import {
  ListItem,
  ListItemContent,
  ListItemTitle,
  ListItemDescription,
  ListItemVisual,
  ListItemExtras,
  ListItemCheckbox,
} from '@/components/ListItem'
import { useTransform } from 'motion/react'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { clamp } from '@/utils/math-util'
import { SearchField } from '@/components/SearchField'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { IconVisual } from '@/components/IconVisual'
import { ScrollShadow } from '@/components/ScrollShadow'
import { SectionEmpty } from '@/features/iam/users/components/SectionEmpty'
import styles from './SelectionList.module.css'

export interface SelectionListProps {
  editing?: boolean
  onSearchTextChange: (text: string) => unknown
  fetchStatus: 'pending' | 'rejected' | 'resolved'
  fetchError?: unknown
  onTryAgain: () => unknown
  isEmpty: boolean
  /**
   * The rendered `SelectionListItem`s.
   */
  children?: ReactNode
  loader?: ReactNode
}

/**
 * This is used as a viewer and editor for skills and roles.
 */
export function SelectionList({
  editing,
  onSearchTextChange,
  fetchStatus,
  fetchError,
  onTryAgain,
  isEmpty,
  children,
  loader,
}: SelectionListProps) {
  const { connectRef: scrollRef, scrollYStart } = useScrollOffsets()
  const shadowOpacity = useTransform(scrollYStart, (v) => clamp(v / 50))
  const renderItems = () =>
    isEmpty ? (
      <SectionEmpty>Nothing matches your search.</SectionEmpty>
    ) : (
      children
    )

  return (
    <div className={cx(styles.root, editing && styles.editing)}>
      {editing && (
        <div className={styles.searchField}>
          <SearchField embedded stretch onChange={onSearchTextChange} />
          <ScrollShadow opacity={shadowOpacity} />
        </div>
      )}
      <div className={styles.items} ref={scrollRef}>
        {editing ? (
          // In Edit-mode, we render all items, which means we fetch first.
          // If not in Edit-model, we can render immediately
          fetchStatus === 'pending' ? (
            <ContentLoading size="section" />
          ) : fetchStatus === 'rejected' ? (
            <ContentError
              size="section"
              error={fetchError}
              onRetry={onTryAgain}
            />
          ) : (
            <>
              {renderItems()}
              {loader}
            </>
          )
        ) : (
          renderItems()
        )}
      </div>
    </div>
  )
}

export interface SelectionListItemProps {
  title: string
  description: string
  icon: ReactNode
  editing?: boolean
  isFirstItem: boolean
  canUpdate: boolean
  checked: boolean
  isTogglePending: boolean
  onToggle: () => unknown
}

export function SelectionListItem({
  title,
  description,
  icon,
  editing,
  isFirstItem,
  canUpdate,
  checked,
  isTogglePending,
  onToggle,
}: SelectionListItemProps) {
  const disabled = isTogglePending || canUpdate === false
  return (
    // Edit-mode renders a search bar.
    <ListItem
      hasListHeader={editing && isFirstItem}
      onPress={editing && canUpdate ? onToggle : undefined}
      isDisabled={disabled}
      pressed={editing ? checked : undefined}
    >
      <ListItemVisual>
        <IconVisual tight>{icon}</IconVisual>
      </ListItemVisual>
      <ListItemContent>
        <ListItemTitle>{title}</ListItemTitle>
        {description && (
          <ListItemDescription title={description}>
            {description}
          </ListItemDescription>
        )}
      </ListItemContent>
      {editing && (
        <ListItemExtras>
          <ListItemCheckbox
            isDisabled={disabled}
            isPending={isTogglePending}
            checked={checked}
          />
        </ListItemExtras>
      )}
    </ListItem>
  )
}
